import { useState, useEffect } from "react";
import api from "../lib/api";
import PageHeader, { PageBody, Card, Badge, useFetch } from "../components/Shared";
import { Megaphone, Plus, Play } from "@phosphor-icons/react";
import { toast } from "sonner";
import { usePipeline, pipelineParams } from "../lib/pipeline";

export default function Campaigns() {
  const { activePipelineSlug } = usePipeline();
  const { data, loading, reload } = useFetch(
    () => api.get("/campaigns", { params: pipelineParams(activePipelineSlug) }).then((r) => r.data),
    [activePipelineSlug]
  );
  const [showCreate, setShowCreate] = useState(false);

  const runCampaign = async (id) => {
    try {
      await api.post(`/campaigns/${id}/run`);
      toast.success("Campaña ejecutada");
      reload();
    } catch (err) {
      toast.error("Error ejecutando campaña");
    }
  };

  return (
    <>
      <PageHeader
        label="Engine"
        title="Campañas"
        description="Motor configurable: broadcast, nurture, reactivación y check-ins automáticos."
      >
        <button onClick={() => setShowCreate(true)} className="bg-terracotta text-white px-4 py-2 rounded text-sm flex items-center gap-2 hover:bg-terracotta-hover" data-testid="create-campaign-btn">
          <Plus size={16} /> Nueva campaña
        </button>
      </PageHeader>
      <PageBody>
        {loading && <div className="text-carbon-500">Cargando...</div>}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {(data || []).map((c) => (
            <Card key={c.id} className="p-5" testid={`campaign-${c.id}`}>
              <div className="flex items-start justify-between mb-3">
                <div className="w-9 h-9 bg-surface-alt rounded flex items-center justify-center">
                  <Megaphone size={18} className="text-terracotta" />
                </div>
                <div className="flex gap-1">
                  <Badge tone={c.active ? "forest" : "neutral"}>{c.active ? "activa" : "pausada"}</Badge>
                  <Badge>{c.type}</Badge>
                </div>
              </div>
              <h3 className="font-heading text-lg mb-1">{c.name}</h3>
              <div className="text-xs text-carbon-500 mb-3 font-mono">{c.frequency}</div>
              {c.message_template && (
                <div className="bg-page border border-carbon-200 rounded p-3 text-sm text-carbon-700 mb-3 italic">
                  "{c.message_template}"
                </div>
              )}
              <div className="grid grid-cols-3 gap-3 text-xs">
                <div>
                  <div className="text-carbon-500">Enviadas</div>
                  <div className="font-mono text-lg">{c.stats?.sent || 0}</div>
                </div>
                <div>
                  <div className="text-carbon-500">Entregadas</div>
                  <div className="font-mono text-lg">{c.stats?.delivered || 0}</div>
                </div>
                <div>
                  <div className="text-carbon-500">Respondieron</div>
                  <div className="font-mono text-lg">{c.stats?.replied || 0}</div>
                </div>
              </div>
              <div className="mt-4 pt-3 border-t border-carbon-200 flex items-center justify-between">
                <div className="text-xs text-carbon-500">
                  Target: {[
                    ...(c.target_segments?.roles || []),
                    ...(c.target_segments?.states || []),
                    ...(c.target_segments?.tags || []),
                  ].join(", ") || "todos"}
                </div>
                <button onClick={() => runCampaign(c.id)} disabled={!c.active} data-testid={`run-campaign-${c.id}`}
                  className="text-xs bg-forest text-white px-3 py-1.5 rounded hover:bg-forest/90 flex items-center gap-1 disabled:opacity-50">
                  <Play size={12} weight="fill" /> Ejecutar
                </button>
              </div>
            </Card>
          ))}
          {data && data.length === 0 && <div className="text-sm text-carbon-500 col-span-full">Sin campañas aún</div>}
        </div>
      </PageBody>

      {showCreate && <CreateCampaignModal onClose={() => setShowCreate(false)} onCreated={() => { setShowCreate(false); reload(); toast.success("Campaña creada"); }} />}
    </>
  );
}

function CreateCampaignModal({ onClose, onCreated }) {
  const { activePipelineSlug } = usePipeline();
  const [form, setForm] = useState({ name: "", type: "broadcast", active: true, frequency: "once", message_template: "", template_id: "" });
  // Filtros avanzados
  const [filters, setFilters] = useState({
    user_sides: [],     // ["migrant_side", "origin_side"]
    roles: "",          // CSV
    states: "",         // CSV
    origin_country_contains: "",
    origin_municipality_contains: "",
    origin_department_contains: "",
    current_country_contains: "",
    current_city_contains: "",
    engagement_range: "any",  // any | 0-0.25 | 0.25-0.5 | 0.5-0.75 | 0.75-1
    stage_keys: [],     // etapas seleccionadas del pipeline activo
    limit: 100,
    order_by: "engagement_desc",
  });
  const [busy, setBusy] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [loadingTpls, setLoadingTpls] = useState(true);
  const [preview, setPreview] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  const [activePipeline, setActivePipeline] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/pipelines");
        setActivePipeline((data || []).find((p) => p.slug === activePipelineSlug) || null);
      } catch { setActivePipeline(null); }
      setFilters((f) => ({ ...f, stage_keys: [] }));
    })();
  }, [activePipelineSlug]);
  const stageOptions = [...((activePipeline?.stages) || [])].sort((a, b) => (a.order || 0) - (b.order || 0));

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/whatsapp/templates", { params: pipelineParams(activePipelineSlug) });
        setTemplates(data || []);
      } catch { setTemplates([]); } finally { setLoadingTpls(false); }
    })();
  }, [activePipelineSlug]);

  const approved = templates.filter((t) => t.status === "approved");
  const otherTpls = templates.filter((t) => t.status !== "approved");

  const onSelectTemplate = (id) => {
    const t = templates.find((x) => x.id === id);
    setForm({ ...form, template_id: id, message_template: t?.body_text || "" });
  };

  const toggleSide = (side) => {
    setFilters((f) => ({
      ...f,
      user_sides: f.user_sides.includes(side)
        ? f.user_sides.filter((s) => s !== side)
        : [...f.user_sides, side],
    }));
  };

  const buildSegments = () => {
    const seg = {
      user_sides: filters.user_sides,
      roles: filters.roles ? filters.roles.split(",").map((x) => x.trim()).filter(Boolean) : [],
      states: filters.states ? filters.states.split(",").map((x) => x.trim()).filter(Boolean) : [],
      tags: [],
      countries: [],
      origin_country_contains: filters.origin_country_contains || null,
      origin_municipality_contains: filters.origin_municipality_contains || null,
      origin_department_contains: filters.origin_department_contains || null,
      current_country_contains: filters.current_country_contains || null,
      current_city_contains: filters.current_city_contains || null,
      limit: Number(filters.limit) > 0 ? Number(filters.limit) : null,
      order_by: filters.order_by,
    };
    if (filters.stage_keys && filters.stage_keys.length) {
      seg.stage_keys = filters.stage_keys;
      if (activePipeline?.id) seg.pipeline_id = activePipeline.id;
    }
    const r = filters.engagement_range;
    if (r === "0-0.25") { seg.engagement_min = 0; seg.engagement_max = 0.25; }
    else if (r === "0.25-0.5") { seg.engagement_min = 0.25; seg.engagement_max = 0.5; }
    else if (r === "0.5-0.75") { seg.engagement_min = 0.5; seg.engagement_max = 0.75; }
    else if (r === "0.75-1") { seg.engagement_min = 0.75; seg.engagement_max = 1.0; }
    return seg;
  };

  const runPreview = async () => {
    if (!form.template_id) { toast.error("Seleccioná una template antes del preview"); return; }
    setPreviewing(true);
    try {
      // Creamos una campaña temporal solo para dry-run, luego la borramos.
      const { data: created } = await api.post("/campaigns", {
        name: `__preview_${Date.now()}`,
        type: form.type,
        active: true,
        frequency: form.frequency,
        message_template: form.message_template,
        template_id: form.template_id,
        pipeline_slug: activePipelineSlug,
        target_segments: buildSegments(),
      });
      const { data: dry } = await api.post(`/campaigns/${created.id}/dry-run`);
      setPreview(dry);
      // Cleanup
      await api.delete(`/campaigns/${created.id}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error en preview");
    } finally { setPreviewing(false); }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.template_id) { toast.error("Seleccioná una template aprobada"); return; }
    setBusy(true);
    try {
      await api.post("/campaigns", {
        name: form.name,
        type: form.type,
        active: form.active,
        frequency: form.frequency,
        message_template: form.message_template,
        template_id: form.template_id,
        pipeline_slug: activePipelineSlug,
        target_segments: buildSegments(),
      });
      onCreated();
    } catch (err) { toast.error(err?.response?.data?.detail || "Error"); } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-carbon-900/60 z-50 flex items-center justify-center p-4">
      <div className="bg-surface border border-carbon-200 rounded-lg w-full max-w-3xl p-6 max-h-[92vh] overflow-y-auto scrollbar-thin">
        <h2 className="font-heading text-2xl mb-4">Nueva campaña</h2>
        <form onSubmit={submit} className="space-y-4">
          {/* ─── Datos básicos ─── */}
          <div className="grid grid-cols-2 gap-3">
            <label className="block col-span-2">
              <span className="section-label">Nombre *</span>
              <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                data-testid="campaign-name-input"
                className="mt-1 w-full bg-page border border-carbon-200 rounded px-3 py-2 text-sm" />
            </label>
            <label className="block">
              <span className="section-label">Tipo</span>
              <select value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })}
                className="mt-1 w-full bg-page border border-carbon-200 rounded px-3 py-2 text-sm">
                <option value="broadcast">broadcast</option>
                <option value="nurture">nurture</option>
                <option value="reactivation">reactivation</option>
                <option value="check_in">check_in</option>
              </select>
            </label>
            <label className="block">
              <span className="section-label">Frecuencia</span>
              <select value={form.frequency} onChange={(e) => setForm({ ...form, frequency: e.target.value })}
                className="mt-1 w-full bg-page border border-carbon-200 rounded px-3 py-2 text-sm">
                <option value="once">once</option>
                <option value="daily">daily</option>
                <option value="weekly">weekly</option>
              </select>
            </label>
          </div>

          {/* ─── Filtros ─── */}
          <div className="border border-carbon-200 rounded p-4 bg-page">
            <div className="section-label mb-3">Filtros de audiencia</div>

            {/* Side */}
            <div className="mb-3">
              <span className="text-xs text-carbon-700 block mb-1">Side</span>
              <div className="flex gap-2">
                {["migrant_side", "origin_side"].map((s) => (
                  <button type="button" key={s} onClick={() => toggleSide(s)}
                    data-testid={`filter-side-${s}`}
                    className={`text-xs px-3 py-1.5 rounded border ${filters.user_sides.includes(s) ? "bg-terracotta text-white border-terracotta" : "border-carbon-200 hover:border-terracotta"}`}>
                    {s}
                  </button>
                ))}
                <span className="text-[10px] text-carbon-500 self-center ml-2">(vacío = todos)</span>
              </div>
            </div>

            {/* Geo origen */}
            <div className="grid grid-cols-3 gap-2 mb-3">
              <label className="block">
                <span className="text-[10px] uppercase tracking-wider text-carbon-500">Origen · país (contains)</span>
                <input value={filters.origin_country_contains} onChange={(e) => setFilters({ ...filters, origin_country_contains: e.target.value })}
                  data-testid="filter-origin-country"
                  placeholder="ej: méx, hond" className="mt-1 w-full bg-surface border border-carbon-200 rounded px-2 py-1.5 text-xs font-mono" />
              </label>
              <label className="block">
                <span className="text-[10px] uppercase tracking-wider text-carbon-500">Origen · municipio (contains)</span>
                <input value={filters.origin_municipality_contains} onChange={(e) => setFilters({ ...filters, origin_municipality_contains: e.target.value })}
                  data-testid="filter-origin-municipality"
                  placeholder="ej: pueb, xela" className="mt-1 w-full bg-surface border border-carbon-200 rounded px-2 py-1.5 text-xs font-mono" />
              </label>
              <label className="block">
                <span className="text-[10px] uppercase tracking-wider text-carbon-500">Origen · departamento (contains)</span>
                <input value={filters.origin_department_contains} onChange={(e) => setFilters({ ...filters, origin_department_contains: e.target.value })}
                  data-testid="filter-origin-department"
                  placeholder="ej: oax, quet" className="mt-1 w-full bg-surface border border-carbon-200 rounded px-2 py-1.5 text-xs font-mono" />
              </label>
            </div>

            {/* Geo residencia */}
            <div className="grid grid-cols-2 gap-2 mb-3">
              <label className="block">
                <span className="text-[10px] uppercase tracking-wider text-carbon-500">Residencia · país (contains)</span>
                <input value={filters.current_country_contains} onChange={(e) => setFilters({ ...filters, current_country_contains: e.target.value })}
                  data-testid="filter-current-country"
                  placeholder="ej: usa" className="mt-1 w-full bg-surface border border-carbon-200 rounded px-2 py-1.5 text-xs font-mono" />
              </label>
              <label className="block">
                <span className="text-[10px] uppercase tracking-wider text-carbon-500">Residencia · ciudad (contains)</span>
                <input value={filters.current_city_contains} onChange={(e) => setFilters({ ...filters, current_city_contains: e.target.value })}
                  data-testid="filter-current-city"
                  placeholder="ej: phoe, hous" className="mt-1 w-full bg-surface border border-carbon-200 rounded px-2 py-1.5 text-xs font-mono" />
              </label>
            </div>

            {/* Roles, States CSV */}
            <div className="grid grid-cols-2 gap-2 mb-3">
              <label className="block">
                <span className="text-[10px] uppercase tracking-wider text-carbon-500">Roles (CSV)</span>
                <input value={filters.roles} onChange={(e) => setFilters({ ...filters, roles: e.target.value })}
                  placeholder="ambassador, founder"
                  className="mt-1 w-full bg-surface border border-carbon-200 rounded px-2 py-1.5 text-xs font-mono" />
              </label>
              <label className="block">
                <span className="text-[10px] uppercase tracking-wider text-carbon-500">Estados (CSV)</span>
                <input value={filters.states} onChange={(e) => setFilters({ ...filters, states: e.target.value })}
                  placeholder="new, active"
                  className="mt-1 w-full bg-surface border border-carbon-200 rounded px-2 py-1.5 text-xs font-mono" />
              </label>
            </div>

            {/* Etapa del pipeline activo */}
            <div className="mb-3">
              <span className="text-xs text-carbon-700 block mb-1">
                Etapas del pipeline <strong>{activePipelineSlug}</strong> {filters.stage_keys.length ? `· ${filters.stage_keys.length} sel.` : "· todas"}
              </span>
              {stageOptions.length ? (
                <div className="flex gap-1 flex-wrap" data-testid="campaign-stage-filter">
                  {stageOptions.map((s) => {
                    const on = filters.stage_keys.includes(s.stage_key);
                    return (
                      <button key={s.stage_key} type="button"
                        onClick={() => setFilters((f) => ({
                          ...f,
                          stage_keys: on ? f.stage_keys.filter((k) => k !== s.stage_key) : [...f.stage_keys, s.stage_key],
                        }))}
                        data-testid={`campaign-stage-${s.stage_key}`}
                        className={`px-2.5 py-1 rounded text-xs border ${on ? "bg-terracotta text-white border-terracotta" : "border-carbon-200 text-carbon-600 hover:border-terracotta"}`}>
                        {s.name}
                      </button>
                    );
                  })}
                </div>
              ) : (
                <div className="text-xs text-carbon-400">Este pipeline no tiene etapas configuradas (se enviará a todo el pipeline).</div>
              )}
              <p className="text-[10px] text-carbon-500 mt-1">Si no seleccionas ninguna, la campaña va a todos los leads del pipeline.</p>
            </div>

            {/* Engagement range */}
            <div className="mb-3">
              <span className="text-xs text-carbon-700 block mb-1">Nivel de engagement</span>
              <div className="flex gap-1 flex-wrap">
                {[
                  { v: "any", label: "Cualquiera" },
                  { v: "0-0.25", label: "Bajo (0–0.25)" },
                  { v: "0.25-0.5", label: "Medio-bajo (0.25–0.5)" },
                  { v: "0.5-0.75", label: "Medio-alto (0.5–0.75)" },
                  { v: "0.75-1", label: "Alto (0.75–1)" },
                ].map((opt) => (
                  <button type="button" key={opt.v} onClick={() => setFilters({ ...filters, engagement_range: opt.v })}
                    data-testid={`filter-engagement-${opt.v}`}
                    className={`text-[10px] px-2 py-1 rounded border ${filters.engagement_range === opt.v ? "bg-forest text-white border-forest" : "border-carbon-200 hover:border-forest"}`}>
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Limit + Order */}
            <div className="grid grid-cols-2 gap-2">
              <label className="block">
                <span className="text-[10px] uppercase tracking-wider text-carbon-500">Cantidad máxima de envíos</span>
                <input type="number" min="0" value={filters.limit}
                  onChange={(e) => setFilters({ ...filters, limit: e.target.value })}
                  data-testid="filter-limit"
                  placeholder="0 = todos, 100 = bloque de 100"
                  className="mt-1 w-full bg-surface border border-carbon-200 rounded px-2 py-1.5 text-xs font-mono" />
                <span className="text-[10px] text-carbon-500">Default <code>100</code>. Pon <code>0</code> para enviar a TODOS los que matcheen.</span>
              </label>
              <label className="block">
                <span className="text-[10px] uppercase tracking-wider text-carbon-500">Orden de envío</span>
                <select value={filters.order_by} onChange={(e) => setFilters({ ...filters, order_by: e.target.value })}
                  data-testid="filter-order-by"
                  className="mt-1 w-full bg-surface border border-carbon-200 rounded px-2 py-1.5 text-xs">
                  <option value="engagement_desc">Mayor engagement primero</option>
                  <option value="recent">Más recientes primero</option>
                  <option value="random">Aleatorio</option>
                </select>
              </label>
            </div>
          </div>

          {/* ─── Template ─── */}
          <div>
            <span className="section-label">Template de WhatsApp (Meta) *</span>
            {loadingTpls ? (
              <div className="text-xs text-carbon-500 mt-2">Cargando templates...</div>
            ) : approved.length === 0 ? (
              <div className="bg-signal/5 border border-signal/30 rounded p-3 mt-1 text-xs">
                <div className="text-signal font-medium mb-1">⚠ No tenés templates aprobadas por Meta en este pipeline</div>
                <div className="text-carbon-600 leading-relaxed">
                  Las campañas solo pueden enviar mensajes outbound fuera de la ventana de 24h usando templates aprobadas.
                  Ve a <a href="/templates" className="text-terracotta underline">Templates WA</a>, crea una y marcala como aprobada cuando Meta confirme.
                </div>
              </div>
            ) : (
              <>
                <select required value={form.template_id} onChange={(e) => onSelectTemplate(e.target.value)}
                  data-testid="campaign-template-select"
                  className="mt-1 w-full bg-page border border-carbon-200 rounded px-3 py-2 text-sm">
                  <option value="">— Seleccioná una template aprobada —</option>
                  {approved.map((t) => (
                    <option key={t.id} value={t.id}>{t.name} · {t.language} · {t.category}{t.pipeline_slug ? ` · ${t.pipeline_slug}` : " · global"}</option>
                  ))}
                </select>
                {form.message_template && (
                  <div className="bg-page border border-carbon-200 rounded p-3 mt-2 text-xs text-carbon-700 whitespace-pre-wrap" data-testid="campaign-template-preview">
                    {form.message_template}
                  </div>
                )}
                <div className="text-[10px] text-carbon-500 mt-1">
                  Solo se listan templates con status <code className="font-mono">approved</code>. {otherTpls.length > 0 && `Hay ${otherTpls.length} más en draft/submitted/rejected que no aparecen aquí.`}
                </div>
              </>
            )}
          </div>

          {/* ─── Preview ─── */}
          <div className="border border-terracotta/30 rounded p-3 bg-terracotta/5">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-medium text-carbon-700">Vista previa de audiencia</span>
              <button type="button" onClick={runPreview} disabled={previewing || !form.template_id}
                data-testid="preview-audience-btn"
                className="text-xs px-3 py-1.5 bg-terracotta text-white rounded hover:bg-terracotta-hover disabled:opacity-50">
                {previewing ? "Calculando…" : "Calcular audiencia"}
              </button>
            </div>
            {preview && (
              <div className="text-xs space-y-2">
                <div data-testid="preview-audience-size">
                  Total de leads que matchean: <strong className="text-2xl text-terracotta">{preview.audience_size}</strong>
                </div>
                <div className="text-carbon-500">
                  Se enviará a:{" "}
                  <strong>{Number(filters.limit) > 0 ? Math.min(Number(filters.limit), preview.audience_size) : preview.audience_size}</strong>{" "}
                  ({filters.order_by === "engagement_desc" ? "mayor engagement primero" : filters.order_by === "recent" ? "más recientes" : "aleatorio"}).
                </div>

                {/* Mensaje renderizado con datos del primer lead real */}
                {preview.sample_message && (
                  <div className="mt-3" data-testid="preview-sample-message">
                    <div className="section-label mb-1 flex items-center justify-between">
                      <span>Mensaje renderizado · ejemplo con el primer lead</span>
                      {preview.users_preview?.[0]?.name && (
                        <span className="text-[10px] text-carbon-500 normal-case tracking-normal">
                          (sustituido con: <strong>{preview.users_preview[0].name}</strong>
                          {preview.users_preview[0].origin_country ? ` · ${preview.users_preview[0].origin_country}` : ""})
                        </span>
                      )}
                    </div>
                    <pre className="text-xs bg-page border border-carbon-200 rounded p-3 whitespace-pre-wrap font-sans text-carbon-800 leading-relaxed">
                      {preview.sample_message}
                    </pre>
                  </div>
                )}

                {preview.users_preview?.length > 0 && (
                  <details className="mt-2">
                    <summary className="cursor-pointer text-terracotta">Ver primeros {Math.min(preview.users_preview.length, 25)} leads</summary>
                    <div className="mt-1 max-h-40 overflow-y-auto text-[10px] space-y-1 font-mono">
                      {preview.users_preview.map((u) => (
                        <div key={u.id}>· {u.name || u.public_id} · {u.origin_country || "—"}/{u.origin_municipality || "—"} · eng={Number(u.engagement_score).toFixed(2)}</div>
                      ))}
                    </div>
                  </details>
                )}
              </div>
            )}
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} className="px-4 py-2 text-sm text-carbon-700 hover:bg-carbon-100 rounded">Cancelar</button>
            <button type="submit" disabled={busy || approved.length === 0}
              data-testid="campaign-create-btn"
              className="px-4 py-2 text-sm bg-terracotta text-white rounded hover:bg-terracotta-hover disabled:opacity-60">
              {busy ? "Creando..." : "Crear"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
